import "./index.css";

import { withLDProvider } from "launchdarkly-react-client-sdk";
import React from "react";
import { createRoot } from "react-dom/client";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { withAuthenticationRequired } from "@auth0/auth0-react";
import { ErrorBoundary } from "@sentry/react";

import { ConfirmOutlet } from "./components/elements/Confirm";
import { GlobalBanners } from "./components/modules/AppBanners";
import { AppUnknownExceptionViewRenderer } from "./components/modules/Error/AppUnknownExceptionView";
import { ClaimPromoCode } from "@/features/promo-code";
import { installChunkPreloadErrorHandler } from "./helpers/chunk-preload-error";
import { removedDeprecatedStorageItems } from "./helpers/storage-maintenance";
import { useMountEffect } from "./hooks/useMountEffect";
import initMonitoring from "./monitoring";
import {
  bootStageTimings,
  isBootStageMarked,
  markBootStage,
} from "./monitoring/bootMarks";
import {
  bootVisibility,
  startBootVisibilityTracking,
} from "./monitoring/bootVisibility";
import { MixpanelProvider, useMixpanel } from "./monitoring/mixpanel";
import EmailNotValidPage from "./pages/Setup/pages/EmailNotValidPage";
import EmailNotVerifiedPage from "./pages/Setup/pages/EmailNotVerifiedPage";
import ApolloProvider from "./providers/ApolloProvider";
import Auth0ProviderWithHistory from "./providers/Auth0ProviderWithHistory";
import { ThemeProvider } from "./providers/ThemeProvider";
import { WeldLoadingProvider } from "./providers/LoadingProvider";
import { ToastProvider } from "./providers/ToastProvider";
import { UserProvider } from "./providers/UserProvider";
import WeldGTMProvider from "./providers/WeldGTMProvider";
import {
  MaintenanceProvider,
  MaintenanceProviderProps,
} from "./providers/operations/MaintenanceProvider";
import Routes from "./routes";
import { AppUpdater } from "./service-worker/AppUpdateToast";
import * as registerServiceWorker from "./service-worker/serviceWorkerRegistration";

declare global {
  interface Window {
    __WELD_APP__: {
      build_date: string | undefined;
      release_id: string | undefined;
      logging?: boolean;
    };
  }
}

window.__WELD_APP__ = {
  build_date: import.meta.env.BUILD_DATE,
  release_id: import.meta.env.RELEASE_ID,
  logging: false,
};

initMonitoring();

startBootVisibilityTracking();

installChunkPreloadErrorHandler();

async function render(content: React.ReactElement) {
  const container = document.getElementById("root");
  const root = createRoot(container!);

  markBootStage("render-start");

  const clientId = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID;
  if (!clientId) {
    throw new Error("Launch Darkly client ID is not set");
  }
  const WeldFeatureFlagProvider = withLDProvider<MaintenanceProviderProps>({
    clientSideID: clientId,
    reactOptions: {
      useCamelCaseFlagKeys: true,
    },
  })(MaintenanceProvider);

  root.render(
    <HelmetProvider>
      <MixpanelProvider>
        <ThemeProvider>
          <WeldLoadingProvider>
            <ToastProvider>
              <Auth0ProviderWithHistory>
                <ApolloProvider>
                  <WeldGTMProvider>
                    <WeldFeatureFlagProvider>
                      <ClaimPromoCode />
                      {content}
                      <ConfirmOutlet />
                    </WeldFeatureFlagProvider>
                  </WeldGTMProvider>
                </ApolloProvider>
              </Auth0ProviderWithHistory>
            </ToastProvider>
          </WeldLoadingProvider>
        </ThemeProvider>
      </MixpanelProvider>
    </HelmetProvider>,
  );
}

const searchParams = new URLSearchParams(window.location.search);
// Read before Auth0 consumes the callback and rewrites the URL: a load that
// exchanges a login code is slower than one that only refreshes a session.
const isAuthRedirectCallback =
  searchParams.has("code") && searchParams.has("state");

function App() {
  const mixpanel = useMixpanel();

  useMountEffect(() => {
    if (isBootStageMarked("app-mounted")) return;
    markBootStage("app-mounted");
    const timings = bootStageTimings();
    const { ms_render_start, ms_app_mounted } = timings;
    mixpanel.track("App Authenticated", {
      auth_gate_ms:
        ms_render_start !== undefined && ms_app_mounted !== undefined
          ? ms_app_mounted - ms_render_start
          : undefined,
      auth_redirect_callback: isAuthRedirectCallback,
      ...timings,
      ...bootVisibility(),
    });
  });

  return (
    <ErrorBoundary fallback={AppUnknownExceptionViewRenderer}>
      <div className="absolute inset-0 flex flex-col">
        <GlobalBanners />
        <div className="relative w-full grow">
          <UserProvider>
            <Helmet>
              <title>Weld</title>
            </Helmet>
            <Routes />
          </UserProvider>
        </div>
      </div>
    </ErrorBoundary>
  );
}

const ProtectedApp = withAuthenticationRequired(App, {
  loginOptions: {
    authorizationParams: {
      screen_hint: window.location.pathname === "/signup" ? "signup" : "login",
      login_hint: searchParams.get("email") ?? undefined,
    },
  },
});

const isEmailNotValidPath =
  window.location.pathname.startsWith("/email-not-valid");
const isEmailNotVerifiedPath = window.location.pathname.startsWith(
  "/email-not-verified",
);

if (isEmailNotValidPath) {
  // Render `email not valid` page, i.e. suspicious email
  render(<EmailNotValidPage />);
} else if (isEmailNotVerifiedPath) {
  // Render `email not verified` page
  render(<EmailNotVerifiedPage />);
} else {
  // Render Weld App
  render(<ProtectedApp />);
}

// Clean up obselete local storage items
removedDeprecatedStorageItems();

const toastContainer = document.getElementById("app-update-toast-container");
const toastRoot = createRoot(toastContainer!);
toastRoot.render(<AppUpdater />);

registerServiceWorker.register({
  checkForUpdatesInterval: 60 * 10 * 1000, // 10 minutes
});
